import { Fragment } from 'react';
import { Box, Skeleton as MuiSkeleton, SxProps, Theme } from '@mui/material';
import { SKELETON_TOKENS } from '@/theme/skeletonTokens';

export type SkeletonVariant = 'text' | 'rect' | 'circle';

export type SkeletonProps = {
  /** text·rect 는 가이드상 모양이 같음 — 이관 diff 가독성 위해 이름만 구분 */
  variant?: SkeletonVariant;
  /** 미지정 시 100%. circle 은 height 와 같은 값 */
  width?: number | string;
  height?: number | string;
  /** circle 에는 적용되지 않음. 숫자는 px */
  radius?: number | string;
  /** 2 이상이면 세로로 반복 */
  count?: number;
  /** count > 1 일 때만 유효 */
  gap?: number | string;
  className?: string;
  sx?: SxProps<Theme>;
};

/**
 * 색·pulse·radius 는 theme/overrides/Skeleton 이 전역으로 공급하므로 여기선 형태만 다룸
 * 부모 styled 에서 스켈레톤 색을 지정하면 공용 색을 덮어버리므로 주의. 위치·크기는 무방
 */
const Skeleton = (props: SkeletonProps) => {
  const {
    variant = 'text',
    width,
    height = SKELETON_TOKENS.height,
    radius,
    count = 1,
    gap = '8px',
    className,
    sx,
  } = props;

  const isCircle = variant === 'circle';
  const resolvedWidth = width ?? (isCircle ? height : '100%');
  // sx 의 borderRadius 는 숫자를 테마 배수로 해석하므로(12 → 48px) px 로 고정
  const resolvedRadius = typeof radius === 'number' ? `${radius}px` : radius;

  const item = (
    <MuiSkeleton
      className={className}
      variant={isCircle ? 'circular' : 'rectangular'}
      width={resolvedWidth}
      height={height}
      aria-hidden
      sx={[
        resolvedRadius !== undefined && !isCircle
          ? { borderRadius: resolvedRadius }
          : {},
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );

  if (count <= 1) return item;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap }}>
      {Array.from({ length: count }, (_, idx) => (
        <Fragment key={idx}>{item}</Fragment>
      ))}
    </Box>
  );
};

export default Skeleton;
